import { supabase } from "@/lib/supabase";

export interface EmailSendResult {
  sent?: boolean;
  skipped?: boolean;
  reason?: string;
  error?: string | null;
  resend_id?: string | null;
}

export interface EmailTemplateMeta {
  code: string;
  label: string;
  icon: string;
  description: string;
}

/**
 * Catálogo de plantillas transaccionales de VendeYa.
 * Cada código es único e identifica un evento concreto de la plataforma.
 */
export const EMAIL_TEMPLATES: EmailTemplateMeta[] = [
  { code: "WELCOME", label: "Bienvenida", icon: "ri-user-heart-line", description: "Se envía al crear la cuenta." },
  { code: "EMAIL_VERIFICATION", label: "Verificación de correo", icon: "ri-mail-check-line", description: "Confirma la dirección de correo." },
  { code: "PASSWORD_RESET", label: "Cambio de contraseña", icon: "ri-lock-line", description: "Solicitud para restablecer contraseña." },
  { code: "SELLER_APPLICATION_SUBMITTED", label: "Solicitud de vendedor recibida", icon: "ri-file-list-3-line", description: "Se recibió la solicitud de vendedor." },
  { code: "SELLER_APPLICATION_REVIEW", label: "Solicitud en revisión", icon: "ri-search-eye-line", description: "La solicitud pasó a revisión." },
  { code: "SELLER_APPROVED", label: "Vendedor aprobado", icon: "ri-store-2-line", description: "La solicitud de vendedor fue aprobada." },
  { code: "SELLER_REJECTED", label: "Vendedor rechazado", icon: "ri-close-circle-line", description: "La solicitud de vendedor fue rechazada." },
  { code: "SELLER_SUSPENDED", label: "Vendedor suspendido", icon: "ri-pause-circle-line", description: "La cuenta de vendedor fue suspendida." },
  { code: "PRODUCT_PUBLISHED", label: "Artículo publicado", icon: "ri-check-double-line", description: "La publicación fue aprobada y publicada." },
  { code: "PRODUCT_REJECTED", label: "Publicación rechazada", icon: "ri-close-circle-line", description: "La publicación necesita cambios." },
  { code: "TICKET_REQUIRED", label: "Falta ticket", icon: "ri-coupon-line", description: "La galería no tiene el ticket requerido." },
  { code: "TICKET_INVALID", label: "Ticket incorrecto", icon: "ri-coupon-3-line", description: "El ticket de una foto no coincide." },
  { code: "PAYMENT_SUCCESS", label: "Pago confirmado", icon: "ri-bank-card-line", description: "El pago fue procesado correctamente." },
  { code: "PAYMENT_FAILED", label: "Pago fallido", icon: "ri-error-warning-line", description: "El pago no pudo completarse." },
  { code: "ORDER_CREATED", label: "Pedido recibido", icon: "ri-shopping-bag-line", description: "Se recibió un nuevo pedido." },
  { code: "ORDER_SHIPPED", label: "Pedido enviado", icon: "ri-truck-line", description: "El pedido fue despachado." },
  { code: "ORDER_DELIVERED", label: "Pedido entregado", icon: "ri-checkbox-circle-line", description: "El pedido fue entregado." },
  { code: "MEMBERSHIP_ACTIVATED", label: "Membresía activada", icon: "ri-verified-badge-line", description: "La verificación VendeYa Verificado está activa." },
  { code: "MEMBERSHIP_EXPIRING", label: "Membresía por vencer", icon: "ri-time-line", description: "La membresía está por vencer." },
  { code: "MEMBERSHIP_EXPIRED", label: "Membresía expirada", icon: "ri-forbid-2-line", description: "La membresía ha expirado." },
  { code: "SECURITY_ALERT", label: "Alerta de seguridad", icon: "ri-shield-flash-line", description: "Actividad inusual en la cuenta." },
];

/**
 * Envía el email de bienvenida al usuario autenticado. Es idempotente: la Edge
 * Function verifica en el servidor si `welcome_email_sent` ya es true.
 */
export async function sendWelcomeEmail(): Promise<EmailSendResult> {
  try {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: { template: "welcome" },
    });
    return error
      ? { error: error.message }
      : ((data as EmailSendResult) ?? {});
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : "Error al enviar el email",
    };
  }
}

export interface SendTransactionalOptions {
  template_code: string;
  recipient?: string;
  user_id?: string;
  variables?: Record<string, unknown>;
  event_key?: string;
}

/**
 * Función central para enviar cualquier correo transaccional.
 * La Edge Function resuelve la plantilla, escapa las variables, evita
 * duplicados por `event_key` y registra el resultado en `email_events`.
 */
export async function sendTransactionalEmail(
  options: SendTransactionalOptions
): Promise<EmailSendResult> {
  try {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: options,
    });
    return error
      ? { error: error.message }
      : ((data as EmailSendResult) ?? {});
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : "Error al enviar el email",
    };
  }
}

/**
 * Envía un email de prueba (solo admin / super_admin autorizado).
 * `testEmail` permite enviar a una dirección distinta a la del admin.
 */
export async function sendTestEmail(
  testTemplate: string,
  testEmail?: string
): Promise<EmailSendResult> {
  try {
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: { template: "test", test_template: testTemplate, test_email: testEmail },
    });
    return error
      ? { error: error.message }
      : ((data as EmailSendResult) ?? {});
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : "Error al enviar el email",
    };
  }
}